import { PrototypeApiError } from './apiTransport';
import type { MediaKind } from './mediaPlayerTypes';
import type { Root } from './prototypeTypes';

export interface MediaNotReadyState {
	kind: MediaKind;
	generationStatus: 'pending' | 'generating';
}

export function mediaNotReadyState(error: unknown): MediaNotReadyState | null {
	if (!(error instanceof PrototypeApiError) || error.status !== 409) return null;
	if (!error.body || typeof error.body !== 'object') return null;
	const body = error.body as { error?: unknown; mediaKind?: unknown; generationStatus?: unknown };
	if (body.error !== 'media_not_ready') return null;
	if (body.mediaKind !== 'audio' && body.mediaKind !== 'video') return null;
	if (body.generationStatus !== 'pending' && body.generationStatus !== 'generating') return null;
	return { kind: body.mediaKind, generationStatus: body.generationStatus };
}

export function presentMediaNotReady(root: Root, kind: MediaKind, error: unknown): boolean {
	const state = mediaNotReadyState(error);
	if (!state || state.kind !== kind) return false;
	if (kind === 'audio') presentAudioNotReady(root);
	return true;
}

export function presentVideoNotReady(
	root: Root,
	chapterIndex: number,
	error: unknown,
	actions: { retry(): void; continueReading(): void },
): boolean {
	const state = mediaNotReadyState(error);
	if (!state || state.kind !== 'video') return false;
	const overlay = root.querySelector<HTMLElement>('#vfReadinessOverlay');
	if (!overlay) return false;
	overlay.classList.add('active');
	overlay.setAttribute('aria-hidden', 'false');
	setText(overlay, '#vfReadinessTitle', `Chapter ${chapterIndex} video is still being prepared`);
	const continueReading = overlay.querySelector<HTMLButtonElement>('#vfContinueReadingBtn');
	if (continueReading) continueReading.onclick = () => {
		clearMediaNotReady(root, 'video');
		actions.continueReading();
	};
	return true;
}

export function clearMediaNotReady(root: Root, kind: MediaKind): void {
	if (kind === 'video') {
		const overlay = root.querySelector<HTMLElement>('#vfReadinessOverlay');
		if (!overlay) return;
		overlay.classList.remove('active');
		overlay.setAttribute('aria-hidden', 'true');
		const continueReading = overlay.querySelector<HTMLButtonElement>('#vfContinueReadingBtn');
		if (continueReading) continueReading.onclick = null;
		return;
	}
	const player = root.querySelector<HTMLElement>('#webPlayer');
	if (!player || player.dataset.mediaReadiness !== 'audio') return;
	delete player.dataset.mediaReadiness;
	const slider = player.querySelector<HTMLInputElement>('#audioProgressSlider');
	if (slider) slider.disabled = false;
	setButtonLabel(player, '#miniPlayBtn', 'Play or pause audio');
	setButtonLabel(player, '#playBtn', 'Play or pause audio');
}

function presentAudioNotReady(root: Root): void {
	const player = root.querySelector<HTMLElement>('#webPlayer');
	if (!player) return;
	player.dataset.mediaReadiness = 'audio';
	setText(player, '#playerTopicName', 'Audio is still being prepared');
	setText(player, '#audioTime', 'Available soon');
	const progress = player.querySelector<HTMLElement>('#playerProgress');
	if (progress) progress.style.width = '0%';
	const slider = player.querySelector<HTMLInputElement>('#audioProgressSlider');
	if (slider) {
		slider.value = '0';
		slider.disabled = true;
		slider.setAttribute('aria-valuetext', 'Audio is still being prepared');
	}
	setButtonLabel(player, '#miniPlayBtn', 'Try audio again');
	setButtonLabel(player, '#playBtn', 'Try audio again');
}

function setText(root: HTMLElement, selector: string, value: string): void {
	const element = root.querySelector<HTMLElement>(selector);
	if (element) element.textContent = value;
}

function setButtonLabel(root: HTMLElement, selector: string, value: string): void {
	root.querySelector<HTMLElement>(selector)?.setAttribute('aria-label', value);
}
